'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { createFormHookContexts, useStore } from '@tanstack/react-form'
import { format as formatDate, isValid } from 'date-fns'
import { identity } from 'es-toolkit'
import { HiMiniChevronDown, HiMiniXMark } from 'react-icons/hi2'
import { HiMiniCheck } from 'react-icons/hi2'
import { MdKeyboardReturn } from 'react-icons/md'
import { twMerge } from 'tailwind-merge'

import { Badge } from '@/components/common/BadgeV2'
import Button from '@/components/common/Button'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxList,
  ComboboxSelectableItem,
  ComboboxTrigger,
} from '@/components/common/ComboboxV2'
import { DaypickerCalendar } from '@/components/common/Daypicker'
import IconButton from '@/components/common/IconButton'
import Popover from '@/components/common/PopoverV2'
import {
  Select,
  SelectContent,
  SelectEmpty,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/Select'
import { Switch } from '@/components/common/SwitchV2'
import { FormTextArea } from '@/components/common/TextAreaV2'
import { FormInput, Input } from '@/components/common/TextInputV2'
import ErrorMessage from '@/components/form/ErrorMessage'
import Label from '@/components/form/Label'
import { UOM_CATEGORIES } from '@/components/library-v2/catalog/ui/Field/constants'
import { TYPE_ICON_MAP } from '@/components/library-v2/catalog/ui/Icon'
import { getCompanyName } from '@/components/library-v2/companies/lib'
import { CompanyAvatar } from '@/components/library-v2/companies/Table/cells'
import { PersonAvatar } from '@/components/library-v2/people/Table/cells'

import { cleanFileName } from '@/helpers/file'

import { useCatalogEntries } from '@/hooks/library/useCatalogEntries'
import { useCompanies } from '@/hooks/library/useCompanies'
import { usePeople } from '@/hooks/library/usePeople'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useOrganizationFiles } from '@/hooks/organization/useOrganizationFiles'

import type { PaginatedResponse } from '@/types/Api'
import type { File, UnhydratedFile } from '@/types/File'
import type {
  EntityCatalogEntry,
  EntityPerson,
  EntityType,
} from '@/types/library/Entity'

// region: constants / helpers

export function formatErrorMessage(err: unknown): string | undefined {
  if (!err) return undefined
  if (typeof err === 'string') return err
  if (Array.isArray(err)) {
    const msgs = err
      .map((e) => (typeof e === 'string' ? e : (e && (e as any).message) || ''))
      .filter(Boolean)
    return msgs.join(', ') || undefined
  }
  if (typeof err === 'object' && (err as any)?.message) {
    return String((err as any).message)
  }
  try {
    return JSON.stringify(err)
  } catch {
    return String(err)
  }
}

// endregion constants / helpers

// region: shared primitives

export interface BaseFieldProps<T> {
  label?: string
  required?: boolean
  value: T
  onChange: (value: T) => void
  onBlur?: () => void
  error?: string
  className?: string
}

export interface FieldContainerProps {
  label?: string
  required?: boolean
  error?: string
  children: React.ReactNode
  className?: string
}

const FieldContainer: React.FC<
  FieldContainerProps & { icon?: React.ReactNode }
> = ({ label, required, error, children, className, icon }) => (
  <div className={twMerge('flex flex-col gap-2', className)}>
    {label && (
      <Label required={required}>
        <div className="flex items-center gap-1.5">
          <div>{icon}</div>
          <span>{label}</span>
        </div>
      </Label>
    )}
    {children}
    {error && <ErrorMessage>{error}</ErrorMessage>}
  </div>
)

// A simple chip used by list inputs
export interface ChipProps {
  children: React.ReactNode
  onRemove?: () => void
  className?: string
}

const Chip: React.FC<ChipProps> = ({ children, onRemove, className }) => (
  <Badge
    variant="outline"
    className={twMerge(
      'flex items-center gap-1 max-w-full truncate font-normal text-[13px]',
      className,
    )}
    onClick={onRemove}
  >
    {children}
    {onRemove && (
      <button
        type="button"
        onClick={onRemove}
        className="transition duration-150 hover:opacity-60"
      >
        <HiMiniXMark size={14} className="stroke-[1px]" />
      </button>
    )}
  </Badge>
)

// endregion shared primitives

// region: input-like combobox trigger

export interface ComboTriggerButtonProps {
  text: string
  isPlaceholder?: boolean
  className?: string
}

const ComboTriggerButton: React.FC<ComboTriggerButtonProps> = ({
  text,
  isPlaceholder = false,
  className,
}) => (
  <button
    type="button"
    className={twMerge(
      'flex h-9 w-full items-center justify-between rounded-md border border-primary-200 hover:border-primary-300 dark:border-primary-800 dark:hover:border-primary-700 bg-primary-50 dark:bg-default-dark px-3 text-sm outline-none transition-[color,box-shadow] focus-visible:border-accent-400 focus-visible:ring-accent-400 dark:focus-visible:border-accent-400/50 dark:focus-visible:ring-accent-400/50 focus-visible:ring-[2px]',
      className,
    )}
  >
    <span
      className={twMerge(
        'truncate',
        isPlaceholder
          ? 'text-primary-500 dark:text-primary-400'
          : 'text-primary-900 dark:text-primary-100',
      )}
    >
      {text}
    </span>
    <HiMiniChevronDown className="flex-shrink-0 h-4 w-4 opacity-60" />
  </button>
)

// endregion input-like combobox trigger

// region: basic fields

export interface StringInputProps extends BaseFieldProps<string> {
  placeholder?: string
}

export const StringInput: React.FC<StringInputProps> = ({
  label,
  required,
  value,
  onChange,
  onBlur,
  error,
  placeholder = 'Enter text...',
  className,
}) => {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value),
    [onChange],
  )
  return (
    <FieldContainer
      label={label}
      required={required}
      error={error}
      className={className}
      icon={TYPE_ICON_MAP.string}
    >
      <FormTextArea
        value={value ?? ''}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={placeholder}
        className="min-h-[54px] dark:bg-default-dark"
        aria-invalid={!!error}
      />
    </FieldContainer>
  )
}

// String() writes numbers below 1e-6 or from 1e21 with an exponent, which these inputs reject.
const toDecimalText = (value: number): string => {
  const text = String(value)
  const match = /^(-?)(\d)(?:\.(\d+))?e([-+]\d+)$/.exec(text)
  if (!match) return text
  const [, sign, lead, fraction = '', exponent] = match
  const digits = lead + fraction
  const point = 1 + Number(exponent)
  return point > 0
    ? `${sign}${digits.padEnd(point, '0')}`
    : `${sign}0.${'0'.repeat(-point)}${digits}`
}

// Blur re-commits the shown text; report only a value that changed.
const useReportChangedNumber = (
  value: number | null | undefined,
  onChange: (next: number | null) => void,
) =>
  useCallback(
    (next: number | null) => {
      if (next !== (value ?? null)) onChange(next)
    },
    [value, onChange],
  )

export interface NumberInputProps extends BaseFieldProps<
  number | null | undefined
> {
  min?: number
  max?: number
  step?: number | 'any'
  placeholder?: string
}

export const NumberInput: React.FC<NumberInputProps> = ({
  label,
  required,
  value,
  onChange,
  onBlur,
  error,
  min,
  max,
  step,
  placeholder = 'Enter number...',
  className,
}) => {
  // Keep local text so decimals like "1." or ".5" are preserved while typing
  const [text, setText] = useState<string>(() =>
    value === null || value === undefined ? '' : toDecimalText(value),
  )

  const allowNegative = typeof min === 'number' ? min < 0 : true

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value
      const pattern = allowNegative ? /^-?\d*(?:\.\d*)?$/ : /^\d*(?:\.\d*)?$/
      if (!pattern.test(next)) return
      setText(next)
    },
    [allowNegative],
  )

  const report = useReportChangedNumber(value, onChange)

  const commit = useCallback(
    (raw: string) => {
      if (raw === '' || raw === '.' || raw === '-' || raw === '-.') {
        report(null)
        setText('')
        return
      }
      const parsed = Number(raw)
      if (!Number.isFinite(parsed)) {
        report(null)
        return
      }
      const clamped = Math.max(
        min ?? -Infinity,
        Math.min(max ?? Infinity, parsed),
      )
      report(clamped)
      setText(toDecimalText(clamped))
    },
    [min, max, report],
  )

  const handleBlurInternal = useCallback(() => {
    commit(text)
    onBlur?.()
  }, [commit, text, onBlur])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        commit(text)
      }
    },
    [commit, text],
  )

  // Sync external numeric value → text when it changes externally
  useEffect(() => {
    if (value === null || value === undefined) {
      setText('')
      return
    }
    const stringValue = toDecimalText(value)
    if (stringValue !== text) {
      setText(stringValue)
    }
  }, [value])

  return (
    <FieldContainer
      label={label}
      required={required}
      error={error}
      className={className}
      icon={TYPE_ICON_MAP.number}
    >
      <FormInput
        type="text"
        inputMode="decimal"
        pattern="[0-9]*[.,]?[0-9]*"
        step={step ?? 'any'}
        value={text}
        onChange={handleTextChange}
        onBlur={handleBlurInternal}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="dark:bg-default-dark"
        aria-invalid={!!error}
      />
    </FieldContainer>
  )
}

export interface BooleanSwitchProps extends BaseFieldProps<boolean> {}

export const BooleanSwitch: React.FC<BooleanSwitchProps> = ({
  label,
  required,
  value,
  onChange,
  onBlur,
  error,
  className,
}) => {
  const handleToggle = useCallback(() => onChange(!value), [value, onChange])
  return (
    <FieldContainer
      label={label}
      required={required}
      error={error}
      className={twMerge(className)}
      icon={TYPE_ICON_MAP.boolean}
    >
      <div className="flex items-center gap-2">
        <Switch checked={!!value} onClick={handleToggle} onBlur={onBlur} />
      </div>
    </FieldContainer>
  )
}

export interface DropdownSelectProps extends BaseFieldProps<string> {
  options: readonly string[]
  placeholder?: string
}

export const DropdownSelect: React.FC<DropdownSelectProps> = ({
  label,
  required,
  value,
  onChange,
  onBlur,
  error,
  options,
  placeholder = 'Select from options...',
  className,
}) => {
  const handleValueChange = useCallback((v: string) => onChange(v), [onChange])
  const handleBlur = useCallback(() => onBlur?.(), [onBlur])
  return (
    <FieldContainer
      label={label}
      required={required}
      error={error}
      className={className}
      icon={TYPE_ICON_MAP.dropdown}
    >
      <Select
        value={value ?? ''}
        onValueChange={handleValueChange}
        onOpenChange={handleBlur}
      >
        <SelectTrigger className="bg-primary-50 dark:bg-default-dark dark:border-primary-800">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.length === 0 ? (
            <SelectEmpty>No options</SelectEmpty>
          ) : (
            options
              .slice()
              .sort()
              .map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))
          )}
        </SelectContent>
      </Select>
    </FieldContainer>
  )
}

// endregion basic fields

// region: list (chip) inputs

interface ListChipsBaseProps<T> extends BaseFieldProps<T[]> {
  placeholder?: string
  addButtonLabel?: string
  unique?: boolean
  type?: 'string' | 'number'
}

const ListChipsBase = <T extends string | number>({
  label,
  required,
  value,
  onChange,
  onBlur,
  error,
  placeholder = 'Type to add...',
  addButtonLabel = 'Add',
  type = 'string',
  unique = true,
  className,
  parse,
  icon,
}: ListChipsBaseProps<T> & {
  parse: (raw: string) => T | null
  icon?: React.ReactNode
}) => {
  const [inputValue, setInputValue] = useState('')

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value)
    },
    [],
  )

  const handleAdd = useCallback(() => {
    const trimmed = inputValue.trim()
    if (!trimmed) return
    const parsed = parse(trimmed)
    if (parsed === null) return
    const next = unique
      ? Array.from(new Set([...(value ?? []), parsed]))
      : [...(value ?? []), parsed]
    onChange(next)
    setInputValue('')
  }, [inputValue, value, onChange, unique, parse])

  // Commit typed text on blur so clicking Save doesn't drop it.
  const handleBlur = useCallback(() => {
    handleAdd()
    onBlur?.()
  }, [handleAdd, onBlur])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleAdd()
      }
      if (
        e.key === 'Backspace' &&
        inputValue === '' &&
        (value?.length ?? 0) > 0
      ) {
        const next = [...(value ?? [])]
        next.pop()
        onChange(next)
      }
    },
    [handleAdd, inputValue, value, onChange],
  )

  const handleRemove = useCallback(
    (idx: number) => () => {
      const next = (value ?? []).filter((_, i) => i !== idx)
      onChange(next)
    },
    [value, onChange],
  )

  return (
    <FieldContainer
      label={label}
      required={required}
      error={error}
      className={className}
      icon={icon}
    >
      <div className="relative flex items-center gap-2">
        <Input
          type={type}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={placeholder}
          className="dark:bg-default-dark flex-1 pr-16"
        />
        <div className="absolute right-[10px]">
          <button
            onClick={handleAdd}
            type="button"
            className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 transition-opacity duration-100 hover:opacity-70"
          >
            <MdKeyboardReturn size={16} />
            <span>{addButtonLabel}</span>
          </button>
        </div>
      </div>
      {(value?.length ?? 0) > 0 && (
        <div className="flex flex-wrap gap-2 w-full max-w-full">
          {(value ?? []).map((item, idx) => (
            <Chip
              key={`${item}-${idx}`}
              onRemove={handleRemove(idx)}
              className="max-w-full truncate"
            >
              <span className="truncate">
                {typeof item === 'string' ? item : item.toLocaleString()}
              </span>
            </Chip>
          ))}
        </div>
      )}
    </FieldContainer>
  )
}

export const StringListChipsInput: React.FC<ListChipsBaseProps<string>> = (
  props,
) => (
  <ListChipsBase<string>
    {...props}
    parse={identity}
    icon={TYPE_ICON_MAP.string_list}
  />
)

const parseNumber = (raw: string) => {
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

export const NumberListChipsInput: React.FC<ListChipsBaseProps<number>> = (
  props,
) => {
  return (
    <ListChipsBase<number>
      {...props}
      type="number"
      parse={parseNumber}
      icon={TYPE_ICON_MAP.number_list}
    />
  )
}

// endregion list (chip) inputs

// region: date & currency

export interface DateInputProps extends BaseFieldProps<string> {
  format?: 'date' | 'datetime'
  placeholder?: string
}

export const DateInput: React.FC<DateInputProps> = ({
  label,
  required,
  value,
  onChange,
  onBlur,
  error,
  format = 'datetime',
  placeholder,
  className,
}) => {
  const [open, setOpen] = useState<boolean>(false)
  const [tempDate, setTempDate] = useState<Date | undefined>(undefined)

  const parseValueToDate = useCallback((): Date | undefined => {
    if (!value) return undefined
    // value like 2025-11-08T16:25 (local), or 2025-11-08 (assume local midnight)
    const date =
      format === 'datetime' ? new Date(value) : new Date(`${value}T00:00`)
    return isValid(date) ? date : undefined
  }, [value, format])

  const dateValue = useMemo(() => parseValueToDate(), [parseValueToDate])

  const icon = TYPE_ICON_MAP.date

  const serializeDate = useCallback(
    (d: Date | undefined): string => {
      if (!d) return ''
      return format === 'datetime'
        ? formatDate(d, "yyyy-MM-dd'T'HH:mm")
        : formatDate(d, 'yyyy-MM-dd')
    },
    [format],
  )

  const displayText = useMemo(() => {
    if (!dateValue) {
      return (
        placeholder ??
        (format === 'datetime' ? 'Select date and time...' : 'Select date...')
      )
    }
    return format === 'datetime'
      ? formatDate(dateValue, 'MMM do, yyyy h:mm a')
      : formatDate(dateValue, 'MMM do, yyyy')
  }, [dateValue, format, placeholder])

  // Initialize tempDate when popover opens
  useEffect(() => {
    if (open) {
      setTempDate(dateValue)
    }
  }, [open, dateValue])

  const handleTempDateSelect = useCallback((d: Date | undefined) => {
    setTempDate(d)
  }, [])

  const handleConfirm = useCallback(() => {
    onChange(serializeDate(tempDate))
    setOpen(false)
    onBlur?.()
  }, [onChange, onBlur, serializeDate, tempDate])

  const handleCancel = useCallback(() => {
    setTempDate(undefined)
    setOpen(false)
  }, [])

  const handleOpenChange = useCallback((newOpen: boolean) => {
    setOpen(newOpen)
    if (!newOpen) {
      // Reset temp date when closing without confirming
      setTempDate(undefined)
    }
  }, [])

  const handleClear = useCallback(() => {
    onChange('')
    onBlur?.()
  }, [onChange, onBlur])

  const triggerClassName = twMerge(
    'h-9 px-3 text-sm rounded-md w-full justify-start shadow-none flex items-center gap-2',
    'bg-primary-50 dark:bg-primary-900 dark:hover:bg-primary-900 border border-primary-200 dark:border-primary-800',
    'text-primary-900 dark:text-primary-100 hover:bg-primary-100',
    'text-left',
    error && 'border-primary-500',
  )

  const disabled = required && tempDate === undefined
  const hasValue = !!dateValue

  return (
    <FieldContainer
      label={label}
      required={required}
      error={error}
      className={className}
      icon={icon}
    >
      <div className="relative flex items-center gap-2">
        <Popover open={open} onOpenChange={handleOpenChange}>
          <Popover.Trigger asChild>
            <button
              type="button"
              className={triggerClassName}
              aria-invalid={!!error}
            >
              {icon}
              <span
                className={twMerge(
                  'flex-1',
                  !dateValue && 'text-primary-500 dark:text-primary-400',
                )}
              >
                {displayText}
              </span>
            </button>
          </Popover.Trigger>
          <Popover.Content align="start" className="p-0" portal={true}>
            <DaypickerCalendar
              date={tempDate}
              onSelect={handleTempDateSelect}
              showTime={format === 'datetime'}
            />
            <div className="flex items-center justify-between gap-2 p-3 border-t border-primary-200 dark:border-primary-700">
              <Button
                variant="outline-compact"
                onClick={handleCancel}
                className="flex-1"
              >
                <HiMiniXMark className="h-4 w-4" />
                <span>Cancel</span>
              </Button>
              <Button
                variant="primary-compact"
                onClick={handleConfirm}
                disabled={disabled}
                className="flex-1"
              >
                <HiMiniCheck className="h-4 w-4" />
                <span>Confirm</span>
              </Button>
            </div>
          </Popover.Content>
        </Popover>
        {hasValue && (
          <IconButton
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1.5"
            aria-label="Clear date"
          >
            <HiMiniXMark className="w-4 h-4" />
          </IconButton>
        )}
      </div>
    </FieldContainer>
  )
}

export interface CurrencyInputProps extends BaseFieldProps<
  number | null | undefined
> {
  currency?: string
  min?: number
  max?: number
  placeholder?: string
}

export const CurrencyInput: React.FC<CurrencyInputProps> = ({
  label,
  required,
  value,
  onChange,
  onBlur,
  error,
  currency,
  min,
  max,
  placeholder = 'Enter amount...',
  className,
}) => {
  const [text, setText] = useState<string>(() =>
    value === null || value === undefined ? '' : toDecimalText(value),
  )

  const normalizeForDisplay = useCallback((raw: string): string => {
    if (raw === '') return '0.00'
    let s = raw
    if (s.startsWith('.')) s = `0${s}`
    const parts = s.split('.')
    const frac = parts[1] ?? ''
    if (frac.length >= 2) return s
    if (parts.length === 1) return `${s}.00`
    return `${parts[0]}.${frac.padEnd(2, '0')}`
  }, [])

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value
      // Allow empty, digits, and a single dot with fractional part
      if (/^\d*(?:\.\d*)?$/.test(next)) {
        setText(next)
      }
    },
    [],
  )

  const report = useReportChangedNumber(value, onChange)

  const commit = useCallback(
    (raw: string) => {
      if (raw === '' || raw === '.') {
        report(null)
        setText('')
        return
      }
      const parsed = Number(raw)
      const clamped = Math.max(
        min ?? -Infinity,
        Math.min(max ?? Infinity, parsed),
      )
      if (Number.isFinite(clamped)) {
        report(clamped)
        setText(normalizeForDisplay(raw))
      }
    },
    [report, min, max, normalizeForDisplay],
  )

  const handleBlur = useCallback(() => {
    commit(text)
    onBlur?.()
  }, [commit, text, onBlur])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        commit(text)
      }
    },
    [commit, text],
  )

  // sync incoming numeric value → formatted text when it changes externally
  useEffect(() => {
    if (value === null || value === undefined) {
      setText('')
      return
    }
    setText(normalizeForDisplay(toDecimalText(value)))
  }, [value, normalizeForDisplay])

  return (
    <FieldContainer
      label={label}
      required={required}
      error={error}
      className={className}
      icon={TYPE_ICON_MAP.currency}
    >
      <div className="relative flex items-center w-full gap-2">
        <FormInput
          type="text"
          inputMode="decimal"
          pattern="[0-9]*[.,]?[0-9]*"
          value={text}
          onChange={handleTextChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="dark:bg-default-dark pr-10 w-full"
          containerClassName="w-full"
          aria-invalid={!!error}
        />
        {currency && (
          <span className="absolute right-[10px] top-[9px] text-sm text-primary-600 dark:text-primary-300 font-mono">
            {currency}
          </span>
        )}
      </div>
    </FieldContainer>
  )
}

// endregion date & currency

// region: multi-select from static options via combobox

export interface StaticMultiSelectComboboxProps extends BaseFieldProps<
  string[]
> {
  options: readonly string[]
  placeholder?: string
}

export const MultiSelectCombobox: React.FC<StaticMultiSelectComboboxProps> = ({
  label,
  required,
  value,
  onChange,
  error,
  options,
  placeholder = 'Select...',
  className,
}) => {
  const [open, setOpen] = useState<boolean>(false)
  const selectedSet = useMemo(() => new Set(value ?? []), [value])
  const handleToggle = useCallback(
    (opt: string) => () => {
      const isSelected = selectedSet.has(opt)
      const next = isSelected
        ? (value ?? []).filter((v) => v !== opt)
        : [...(value ?? []), opt]
      onChange(next)
    },
    [selectedSet, value, onChange],
  )

  return (
    <FieldContainer
      label={label}
      required={required}
      error={error}
      className={className}
      icon={TYPE_ICON_MAP.multi_select}
    >
      <Combobox open={open} onOpenChange={setOpen}>
        <ComboboxTrigger asChild={true}>
          <div className="w-full">
            <ComboTriggerButton
              text={
                (value?.length ?? 0) > 0
                  ? `${value?.length} selected`
                  : (placeholder ?? 'Select...')
              }
              isPlaceholder={(value?.length ?? 0) === 0}
            />
          </div>
        </ComboboxTrigger>
        {open && (
          <ComboboxContent align="start" className="p-0">
            <ComboboxInput
              placeholder="Search..."
              className="placeholder:text-[13px]"
            />
            <ComboboxList>
              <ComboboxEmpty className="text-[13px]">No options</ComboboxEmpty>
              <ComboboxGroup>
                {options.map((opt) => (
                  <ComboboxSelectableItem
                    key={opt}
                    value={opt}
                    isSelected={selectedSet.has(opt)}
                    onSelect={handleToggle(opt)}
                  >
                    <span className="text-[13px] font-medium truncate">
                      {opt}
                    </span>
                  </ComboboxSelectableItem>
                ))}
              </ComboboxGroup>
            </ComboboxList>
          </ComboboxContent>
        )}
      </Combobox>
    </FieldContainer>
  )
}

// endregion multi-select from static options via combobox

// region: files combobox

export interface FileComboboxProps extends BaseFieldProps<
  string | string[] | undefined
> {
  multiple?: boolean
  placeholder?: string
}

const selectSortedFiles = (data: PaginatedResponse<UnhydratedFile>) =>
  data.data.slice().sort((a, b) => a.name.localeCompare(b.name))

export const FileCombobox: React.FC<FileComboboxProps> = ({
  label,
  required,
  value,
  onChange,
  error,
  multiple = false,
  placeholder = 'Select file...',
  className,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { data: files, isLoading } = useOrganizationFiles({
    orgId: activeOrgId,
    selector: selectSortedFiles,
  })

  const [open, setOpen] = useState(false)
  const selectedIds: string[] = Array.isArray(value)
    ? value
    : value
      ? [value]
      : []

  const handleChange = useCallback(
    (ids: string[] | null) => {
      if (multiple) {
        onChange(ids ?? [])
      } else {
        onChange(ids && ids.length > 0 ? ids[0] : '')
      }
      setOpen(false)
    },
    [multiple, onChange],
  )

  const toggleId = useCallback(
    (id: string) => () => {
      const isSelected = selectedIds.includes(id)
      if (!multiple) {
        handleChange(isSelected ? null : [id])
        return
      }
      const next = isSelected
        ? selectedIds.filter((v) => v !== id)
        : [...selectedIds, id]
      handleChange(next)
    },
    [selectedIds, handleChange, multiple],
  )

  const selectedFiles = useMemo(
    () => (files ?? []).filter((f) => selectedIds.includes(f.id)),
    [files, selectedIds],
  )

  return (
    <FieldContainer
      label={label}
      required={required}
      error={error}
      className={className}
    >
      <Combobox open={open} onOpenChange={setOpen}>
        <ComboboxTrigger asChild={true}>
          <div className="w-full">
            <ComboTriggerButton
              text={
                selectedFiles.length > 0
                  ? multiple
                    ? `${selectedFiles.length} files`
                    : (selectedFiles[0]?.name ?? '')
                  : placeholder
              }
              isPlaceholder={selectedFiles.length === 0}
            />
          </div>
        </ComboboxTrigger>
        {open && (
          <ComboboxContent align="start" className="p-0 sm:max-w-[320px]">
            <ComboboxInput
              placeholder="Search files..."
              className="placeholder:text-[13px]"
            />
            <ComboboxList>
              <ComboboxEmpty className="text-[13px]">
                {isLoading ? 'Loading...' : 'No files found.'}
              </ComboboxEmpty>
              <ComboboxGroup>
                {(files ?? []).map((file) => {
                  const isSelected = selectedIds.includes(file.id)
                  return (
                    <ComboboxSelectableItem
                      key={file.id}
                      value={file.name}
                      isSelected={isSelected}
                      onSelect={toggleId(file.id)}
                    >
                      <div className="flex items-center gap-x-2 max-w-64 truncate">
                        <span className="text-[13px] font-medium truncate">
                          {cleanFileName(file.name)}
                        </span>
                      </div>
                    </ComboboxSelectableItem>
                  )
                })}
              </ComboboxGroup>
            </ComboboxList>
          </ComboboxContent>
        )}
      </Combobox>
    </FieldContainer>
  )
}

// endregion: files combobox

// region: reference combobox (person/company/agency/catalog_entry)

export interface ReferenceComboboxProps extends BaseFieldProps<
  string | string[] | undefined
> {
  referenceType: EntityType
  multiple?: boolean
  placeholder?: string
}

const getPersonLabel = (person: EntityPerson) => {
  return `${person.first_name ?? ''} ${person.last_name ?? ''}`.trim()
}

const getCatalogEntryLabel = (entry: EntityCatalogEntry) => {
  return entry.name ?? 'Untitled'
}

export const ReferenceCombobox: React.FC<ReferenceComboboxProps> = ({
  label,
  required,
  value,
  onChange,
  error,
  referenceType,
  multiple = false,
  placeholder = 'Select...',
  className,
}) => {
  const { activeOrgId } = useActiveOrgId()

  const [open, setOpen] = useState(false)

  const selectedIds: string[] = Array.isArray(value)
    ? value
    : value
      ? [value]
      : []

  // fetch entities according to referenceType
  const { data: companies } = useCompanies({
    orgId: activeOrgId,
  })
  const { data: people } = usePeople({
    orgId: activeOrgId,
  })
  const { data: entries } = useCatalogEntries({
    orgId: activeOrgId,
  })

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])

  const handleChange = useCallback(
    (ids: string[] | null) => {
      if (multiple) {
        onChange(ids ?? [])
      } else {
        onChange(ids && ids.length > 0 ? ids[0] : '')
      }
      setOpen(false)
    },
    [multiple, onChange],
  )

  const toggleId = useCallback(
    (id: string) => () => {
      const isSelected = selectedSet.has(id)
      if (!multiple) {
        handleChange(isSelected ? null : [id])
        return
      }
      const next = isSelected
        ? selectedIds.filter((v) => v !== id)
        : [...selectedIds, id]
      handleChange(next)
    },
    [selectedIds, selectedSet, handleChange, multiple],
  )

  const selectedLabels = useMemo(() => {
    switch (referenceType) {
      case 'company':
        return (companies ?? [])
          .filter((i) => selectedSet.has(i.id))
          .map(getCompanyName)
      case 'person':
        return (people ?? [])
          .filter((i) => selectedSet.has(i.id))
          .map(getPersonLabel)
      case 'catalog_entry':
        return (entries ?? [])
          .filter((i) => selectedSet.has(i.id))
          .map(getCatalogEntryLabel)
      default:
        return []
    }
  }, [referenceType, companies, people, entries, selectedSet])

  return (
    <FieldContainer
      label={label}
      required={required}
      error={error}
      className={className}
    >
      <Combobox open={open} onOpenChange={setOpen}>
        <ComboboxTrigger asChild={true}>
          <div className="w-full">
            <ComboTriggerButton
              text={
                selectedLabels.length > 0
                  ? multiple
                    ? `${selectedLabels.length} selected`
                    : selectedLabels[0]
                  : (placeholder ?? 'Select...')
              }
              isPlaceholder={selectedLabels.length === 0}
            />
          </div>
        </ComboboxTrigger>
        {open && (
          <ComboboxContent align="start" className="p-0 sm:max-w-[320px]">
            <ComboboxInput
              placeholder="Search..."
              className="placeholder:text-[13px]"
            />
            <ComboboxList>
              <ComboboxEmpty className="text-[13px]">
                {referenceType === 'agency'
                  ? 'Agencies not available yet.'
                  : 'No results.'}
              </ComboboxEmpty>
              <ComboboxGroup>
                {referenceType === 'company' &&
                  (companies ?? []).map((item) => {
                    const label = getCompanyName(item)
                    return (
                      <ComboboxSelectableItem
                        key={item.id}
                        value={label}
                        isSelected={selectedSet.has(item.id)}
                        onSelect={toggleId(item.id)}
                      >
                        <div className="flex items-center gap-x-2 max-w-64 truncate">
                          <CompanyAvatar
                            company={item}
                            width={22}
                            height={22}
                          />
                          <span className="text-[13px] font-medium truncate">
                            {label}
                          </span>
                        </div>
                      </ComboboxSelectableItem>
                    )
                  })}
                {referenceType === 'person' &&
                  (people ?? []).map((item) => {
                    const label = getPersonLabel(item)
                    return (
                      <ComboboxSelectableItem
                        key={item.id}
                        value={label}
                        isSelected={selectedSet.has(item.id)}
                        onSelect={toggleId(item.id)}
                      >
                        <div className="flex items-center gap-x-2 max-w-64 truncate">
                          <PersonAvatar person={item} width={22} height={22} />
                          <span className="text-[13px] font-medium truncate">
                            {label}
                          </span>
                        </div>
                      </ComboboxSelectableItem>
                    )
                  })}
                {referenceType === 'catalog_entry' &&
                  (entries ?? []).map((item) => {
                    const label = getCatalogEntryLabel(item)
                    return (
                      <ComboboxSelectableItem
                        key={item.id}
                        value={label}
                        isSelected={selectedSet.has(item.id)}
                        onSelect={toggleId(item.id)}
                      >
                        <div className="flex items-center gap-x-2 max-w-64 truncate">
                          <span className="text-[13px] font-medium truncate">
                            {label}
                          </span>
                        </div>
                      </ComboboxSelectableItem>
                    )
                  })}
              </ComboboxGroup>
            </ComboboxList>
          </ComboboxContent>
        )}
      </Combobox>
    </FieldContainer>
  )
}

// endregion: reference combobox

// region: Unit of Measure combobox (grouped + search)

export interface UnitOfMeasureComboboxProps extends BaseFieldProps<string> {
  placeholder?: string
}

const allUnits = UOM_CATEGORIES.flatMap((g) => g.units)

export const UnitOfMeasureCombobox: React.FC<UnitOfMeasureComboboxProps> = ({
  label,
  required,
  value,
  onChange,
  error,
  placeholder = 'Select unit...',
  className,
}) => {
  const [open, setOpen] = useState(false)

  const selectedLabel = value && allUnits.includes(value) ? value : ''

  const handleSelect = useCallback(
    (unit: string) => () => {
      onChange(unit)
      setOpen(false)
    },
    [onChange],
  )

  return (
    <FieldContainer
      label={label}
      required={required}
      error={error}
      className={className}
    >
      <Combobox open={open} onOpenChange={setOpen}>
        <ComboboxTrigger asChild={true}>
          <div className="w-full">
            <ComboTriggerButton
              text={selectedLabel || placeholder}
              isPlaceholder={selectedLabel === ''}
            />
          </div>
        </ComboboxTrigger>
        {open && (
          <ComboboxContent align="start" className="p-0 sm:max-w-[360px]">
            <ComboboxInput
              placeholder="Search units..."
              className="placeholder:text-[13px]"
            />
            <ComboboxList className="max-h-[360px] overflow-y-auto">
              <ComboboxEmpty className="text-[13px]">
                No units found.
              </ComboboxEmpty>
              {UOM_CATEGORIES.map((group, idx) => (
                <ComboboxGroup key={idx}>
                  <div className="px-4 py-[6px] text-xs font-semibold bg-primary-100 dark:bg-primary-800 text-primary-900 dark:text-primary-100 -mx-2 my-1">
                    {group.name}
                  </div>
                  {group.units.map((u) => (
                    <ComboboxSelectableItem
                      key={u}
                      value={u}
                      isSelected={value === u}
                      onSelect={handleSelect(u)}
                    >
                      <span className="text-[13px] font-medium truncate">
                        {u}
                      </span>
                    </ComboboxSelectableItem>
                  ))}
                </ComboboxGroup>
              ))}
            </ComboboxList>
          </ComboboxContent>
        )}
      </Combobox>
    </FieldContainer>
  )
}

// endregion UOM combobox

// region: description textarea (helper)

export interface DescriptionTextAreaProps extends BaseFieldProps<string> {
  placeholder?: string
}

export const DescriptionTextArea: React.FC<DescriptionTextAreaProps> = ({
  label,
  required,
  value,
  onChange,
  onBlur,
  error,
  placeholder = 'Enter description...',
  className,
}) => {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value),
    [onChange],
  )
  return (
    <FieldContainer
      label={label}
      required={required}
      error={error}
      className={className}
      icon={TYPE_ICON_MAP.string}
    >
      <FormTextArea
        value={value ?? ''}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={placeholder}
        className="min-h-[96px] dark:bg-default-dark"
        aria-invalid={!!error}
      />
    </FieldContainer>
  )
}

// endregion description

// region: TanStack React Form adapters factory

interface CreateAdaptersArgs {
  useFieldContext: ReturnType<typeof createFormHookContexts>['useFieldContext']
}

export function createTanstackFormFieldComponents({
  useFieldContext,
}: CreateAdaptersArgs) {
  interface StringFieldControlProps {
    label: string
    placeholder?: string
    required?: boolean
  }
  const StringFieldControl: React.FC<StringFieldControlProps> = ({
    label,
    placeholder,
    required,
  }) => {
    const field = useFieldContext<string>()
    const rawError = useStore(field.store, (s) => s.meta.errors)
    const error = formatErrorMessage(rawError)
    return (
      <StringInput
        label={label}
        placeholder={placeholder}
        required={required}
        value={field.state.value}
        onChange={field.handleChange}
        onBlur={field.handleBlur}
        error={error}
      />
    )
  }

  interface NumberFieldControlProps {
    label: string
    required?: boolean
    min?: number
    max?: number
    step?: number
  }
  const NumberFieldControl: React.FC<NumberFieldControlProps> = ({
    label,
    required,
    min,
    max,
    step,
  }) => {
    const field = useFieldContext<number | null | undefined>()
    const rawError = useStore(field.store, (s) => s.meta.errors)
    const error = formatErrorMessage(rawError)
    return (
      <NumberInput
        label={label}
        required={required}
        min={min}
        max={max}
        step={step}
        value={field.state.value}
        onChange={field.handleChange}
        onBlur={field.handleBlur}
        error={error}
      />
    )
  }

  interface BooleanFieldControlProps {
    label: string
    required?: boolean
  }
  const BooleanFieldControl: React.FC<BooleanFieldControlProps> = ({
    label,
    required,
  }) => {
    const field = useFieldContext<boolean | undefined>()
    const rawError = useStore(field.store, (s) => s.meta.errors)
    const error = formatErrorMessage(rawError)
    return (
      <BooleanSwitch
        label={label}
        required={required}
        value={!!field.state.value}
        onChange={field.handleChange}
        onBlur={field.handleBlur}
        error={error}
      />
    )
  }

  interface DropdownFieldControlProps {
    label: string
    required?: boolean
    options: readonly string[]
  }
  const DropdownFieldControl: React.FC<DropdownFieldControlProps> = ({
    label,
    required,
    options,
  }) => {
    const field = useFieldContext<string | undefined>()
    const rawError = useStore(field.store, (s) => s.meta.errors)
    const error = formatErrorMessage(rawError)
    return (
      <DropdownSelect
        label={label}
        required={required}
        options={options}
        value={field.state.value ?? ''}
        onChange={field.handleChange}
        onBlur={field.handleBlur}
        error={error}
      />
    )
  }

  interface MultiSelectFieldControlProps {
    label: string
    required?: boolean
    options: readonly string[]
  }
  const MultiSelectFieldControl: React.FC<MultiSelectFieldControlProps> = ({
    label,
    required,
    options,
  }) => {
    const field = useFieldContext<string[] | undefined>()
    const rawError = useStore(field.store, (s) => s.meta.errors)
    const error = formatErrorMessage(rawError)
    return (
      <MultiSelectCombobox
        label={label}
        required={required}
        options={options}
        value={field.state.value ?? []}
        onChange={field.handleChange}
        error={error}
      />
    )
  }

  interface ListFieldControlProps {
    label: string
    required?: boolean
    isNumber?: boolean
  }
  const ListFieldControl: React.FC<ListFieldControlProps> = ({
    label,
    required,
    isNumber,
  }) => {
    const field = useFieldContext<string[] | number[] | undefined>()
    const rawError = useStore(field.store, (s) => s.meta.errors)
    const error = formatErrorMessage(rawError)
    if (isNumber) {
      return (
        <NumberListChipsInput
          type="number"
          label={label}
          required={required}
          value={(field.state.value ?? []).map(Number)}
          onChange={field.handleChange}
          error={error}
        />
      )
    }
    return (
      <StringListChipsInput
        type="string"
        label={label}
        required={required}
        value={(field.state.value ?? []) as string[]}
        onChange={field.handleChange}
        error={error}
      />
    )
  }

  interface StringListChipsFieldControlProps {
    label: string
    required?: boolean
  }
  const StringListChipsFieldControl: React.FC<
    StringListChipsFieldControlProps
  > = ({ label, required }) => {
    const field = useFieldContext<string[] | undefined>()
    const rawError = useStore(field.store, (s) => s.meta.errors)
    const error = formatErrorMessage(rawError)
    return (
      <StringListChipsInput
        type="string"
        label={label}
        required={required}
        value={field.state.value ?? []}
        onChange={field.handleChange}
        error={error}
      />
    )
  }

  interface NumberListChipsFieldControlProps {
    label: string
    required?: boolean
  }
  const NumberListChipsFieldControl: React.FC<
    NumberListChipsFieldControlProps
  > = ({ label, required }) => {
    const field = useFieldContext<number[] | undefined>()
    const rawError = useStore(field.store, (s) => s.meta.errors)
    const error = formatErrorMessage(rawError)

    const handleNumberListChange = useCallback(
      (v: number[]) => {
        field.handleChange(v as any)
      },
      [field],
    )

    return (
      <NumberListChipsInput
        type="number"
        label={label}
        required={required}
        value={field.state.value ?? []}
        onChange={handleNumberListChange}
        error={error}
      />
    )
  }

  interface DateFieldControlProps {
    label: string
    required?: boolean
    format?: 'date' | 'datetime'
  }
  const DateFieldControl: React.FC<DateFieldControlProps> = ({
    label,
    required,
    format,
  }) => {
    const field = useFieldContext<string | undefined>()
    const rawError = useStore(field.store, (s) => s.meta.errors)
    const error = formatErrorMessage(rawError)
    return (
      <DateInput
        label={label}
        required={required}
        format={format}
        value={field.state.value ?? ''}
        onChange={field.handleChange}
        onBlur={field.handleBlur}
        error={error}
      />
    )
  }

  interface CurrencyFieldControlProps {
    label: string
    required?: boolean
    currency?: string
    min?: number
    max?: number
  }
  const CurrencyFieldControl: React.FC<CurrencyFieldControlProps> = ({
    label,
    required,
    currency,
    min,
    max,
  }) => {
    const field = useFieldContext<number | null | undefined>()
    const rawError = useStore(field.store, (s) => s.meta.errors)
    const error = formatErrorMessage(rawError)
    return (
      <CurrencyInput
        label={label}
        required={required}
        currency={currency}
        min={min}
        max={max}
        value={field.state.value}
        onChange={field.handleChange}
        onBlur={field.handleBlur}
        error={error}
      />
    )
  }

  interface FileFieldControlProps {
    label: string
    required?: boolean
    multiple?: boolean
  }
  const FileFieldControl: React.FC<FileFieldControlProps> = ({
    label,
    required,
    multiple,
  }) => {
    const field = useFieldContext<string | string[] | undefined>()
    const rawError = useStore(field.store, (s) => s.meta.errors)
    const error = formatErrorMessage(rawError)
    return (
      <FileCombobox
        label={label}
        required={required}
        multiple={multiple}
        value={field.state.value ?? (multiple ? [] : '')}
        onChange={field.handleChange}
        error={error}
      />
    )
  }

  interface ReferenceFieldControlProps {
    label: string
    required?: boolean
    multiple?: boolean
    referenceType: 'company' | 'person' | 'agency' | 'catalog_entry'
  }
  const ReferenceFieldControl: React.FC<ReferenceFieldControlProps> = ({
    label,
    required,
    multiple,
    referenceType,
  }) => {
    const field = useFieldContext<string | string[] | undefined>()
    const rawError = useStore(field.store, (s) => s.meta.errors)
    const error = formatErrorMessage(rawError)
    return (
      <ReferenceCombobox
        label={label}
        required={required}
        multiple={multiple}
        referenceType={referenceType}
        value={field.state.value ?? (multiple ? [] : '')}
        onChange={field.handleChange}
        error={error}
      />
    )
  }

  interface UnitOfMeasureComboboxControlProps {
    label: string
    required?: boolean
  }
  const UnitOfMeasureComboboxControl: React.FC<
    UnitOfMeasureComboboxControlProps
  > = ({ label, required }) => {
    const field = useFieldContext<string>()
    const rawError = useStore(field.store, (s) => s.meta.errors)
    const error = formatErrorMessage(rawError)
    return (
      <UnitOfMeasureCombobox
        label={label}
        required={required}
        value={field.state.value ?? ''}
        onChange={field.handleChange}
        error={error}
      />
    )
  }

  return {
    StringFieldControl,
    NumberFieldControl,
    BooleanFieldControl,
    DropdownFieldControl,
    MultiSelectFieldControl,
    ListFieldControl,
    DateFieldControl,
    CurrencyFieldControl,
    FileFieldControl,
    ReferenceFieldControl,
    UnitOfMeasureComboboxControl,
    StringListChipsFieldControl,
    NumberListChipsFieldControl,
  }
}

// endregion: TanStack React Form adapters factory
